import { Clock, Eye, PenLine, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { badgeBase, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
	ageMinutes,
	CATEGORY_LABEL,
	type Category,
	formatDateTime,
	formatMinutes,
	helpdeskApi,
	type InboxItem,
	type InboxStatus,
	reactionMinutes,
	STATUS_LABEL,
	useHelpdeskMutation,
	type Viewer,
} from "./api";

/** `failed` → z powrotem do kolejki wysyłki (nowy cykl prób, ten sam Message-ID). */
export function ResendButton({ articleId }: { articleId: number }) {
	const resend = useHelpdeskMutation(() => helpdeskApi.resendArticle(articleId), {
		success: "Wiadomość wróciła do kolejki wysyłki",
		error: "Nie udało się ponowić wysyłki",
	});
	return (
		<Button
			size="sm"
			variant="ghost"
			className="h-5 shrink-0 gap-1 px-1.5 text-[11px]"
			// Po sukcesie do odświeżenia listy: drugi klik dałby tylko 409.
			disabled={resend.isPending || resend.isSuccess}
			onClick={() => resend.mutate(undefined)}
		>
			<RotateCcw className="size-3" />
			Wyślij ponownie
		</Button>
	);
}

/**
 * Wiek jak w OTRS: od wpłynięcia, przy otwartym biegnie (co 30 s), przy zakończonym stoi.
 * Ostrzeżenie, dopóki nikt nie zareagował.
 */
export function TicketAge({
	item,
}: {
	item: Pick<InboxItem, "openedAt" | "firstTouchAt" | "closedAt" | "status">;
}) {
	const running = item.status !== "closed";
	const [now, setNow] = useState(Date.now);
	useEffect(() => {
		if (!running) return;
		const id = setInterval(() => setNow(Date.now()), 30_000);
		return () => clearInterval(id);
	}, [running]);
	const age = formatMinutes(ageMinutes(item, now));
	if (running && !item.firstTouchAt) {
		return (
			<span
				className="inline-flex items-center gap-1 font-medium tabular-nums text-status-warning-text"
				title={`Wpłynęło ${formatDateTime(item.openedAt)}, nikt jeszcze nie zareagował`}
			>
				<Clock className="size-3.5 shrink-0" aria-hidden />
				<span className="sr-only">bez reakcji od </span>
				{age}
			</span>
		);
	}
	return (
		<span
			className="tabular-nums text-muted-foreground"
			title={`Wpłynęło ${formatDateTime(item.openedAt)}${running ? "" : `, zakończone ${formatDateTime(item.closedAt)}`}`}
		>
			{age}
		</span>
	);
}

/** Czas od wpłynięcia do pierwszego działania pracownika albo „brak”. */
export function ReactionTime({ item }: { item: Pick<InboxItem, "openedAt" | "firstTouchAt"> }) {
	const minutes = reactionMinutes(item);
	if (minutes === null) {
		return <span className="font-medium text-status-warning-text">brak</span>;
	}
	return (
		<span
			className="tabular-nums text-muted-foreground"
			title={`Pierwsza reakcja ${formatDateTime(item.firstTouchAt)}`}
		>
			po {formatMinutes(minutes)}
		</span>
	);
}

/** Inni pracownicy przy zgłoszeniu teraz; ostrzeżenie, gdy ktoś już pisze (odpowiedź albo notatkę). */
export function PresenceBadge({ viewers }: { viewers: Viewer[] }) {
	if (!viewers.length) return null;
	const writing = viewers.some((v) => v.writing);
	const Icon = writing ? PenLine : Eye;
	const short = viewers.map((v) => `${v.name.split(" ")[0]} ${v.writing ? "pisze" : "ogląda"}`);
	const full = viewers.map(
		(v) => `${v.name} ${v.writing ? "pisze w tym zgłoszeniu" : "ma otwarte to zgłoszenie"}`,
	);
	return (
		<span
			className={cn(badgeBase, "shrink-0 gap-1", statusClasses[writing ? "warning" : "info"])}
			title={full.join(", ")}
		>
			<Icon className="size-3" aria-hidden />
			<span className="sr-only">{full.join(", ")}</span>
			<span aria-hidden>{short.join(", ")}</span>
		</span>
	);
}

export function CategoryBadge({ category }: { category: Category }) {
	const tone =
		category === "alerty"
			? "warning"
			: category === "robaczek" || category === "pomysly"
				? "info"
				: "success";
	return <span className={cn(badgeBase, statusClasses[tone])}>{CATEGORY_LABEL[category]}</span>;
}

export function StatusBadge({
	status,
	unseen,
	reply,
}: {
	status: InboxStatus;
	unseen?: boolean;
	/** Otwarte z nieprzeczytaną odpowiedzią apteki: „Odpowiedź” zamiast „Otwarte”. */
	reply?: boolean;
}) {
	return (
		<span
			className={cn(
				badgeBase,
				status === "new" || reply
					? "border-primary bg-primary text-primary-foreground"
					: status === "open"
						? statusClasses.info
						: "border-border bg-muted text-muted-foreground",
			)}
		>
			{reply ? "Odpowiedź" : STATUS_LABEL[status]}
			{/* Zielony wiersz skrzynki — dla czytnika ekranu słowem. */}
			{unseen ? <span className="sr-only">, nieotwarte</span> : null}
		</span>
	);
}
